import type { TFactTrajectoryStationWriteDto, TFactTrajectoryWriteDto } from '../../types/fact-trajectory-write';
import type { TTrajectory, TTrajectoryMeasure, TTrajectoryStation, TTrajectoryValue } from '../../types/trajectory';
import type { FactTrajectoryEntity, TFactTrajectoryData } from '../entities';
import { FACT_TRAJECTORY_POINT_KEYS, type TFactTrajectoryPoint } from '../model';

import { mapApiStationToFactPoint } from './map-api-trajectory';
import { resolveActiveFlag } from './map-fact-trajectory';

/** Local new trajectories / stations use negative or missing ids that must not be sent to the backend. */
function resolveWriteId(id: number | null | undefined): number | undefined {
  if (id == null || id <= 0) {
    return undefined;
  }

  return id;
}

function numericField(value: number | null | undefined): number | undefined {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return undefined;
  }

  return value;
}

function measureValue(measure: { value: number } | undefined | null): number | undefined {
  return numericField(measure?.value);
}

function readTrajectoryValue(value: number | TTrajectoryValue | undefined | null): number | undefined {
  if (typeof value === 'number') {
    return numericField(value);
  }

  return measureValue(value);
}

function toTrajectoryValue(value: number | TTrajectoryValue | undefined | null): TTrajectoryValue | undefined {
  const numericValue = readTrajectoryValue(value);

  if (numericValue === undefined) {
    return undefined;
  }

  return { value: numericValue };
}

function measureUom(measure: { uom?: string } | undefined | null): string | undefined {
  return measure?.uom || undefined;
}

function readBaseReferencePointUuid(
  datum: { baseReferencePoint?: { uuid?: unknown } | Record<string, unknown> } | null | undefined,
): string | undefined {
  const base = datum?.baseReferencePoint;

  if (!base || typeof base !== 'object' || !('uuid' in base)) {
    return undefined;
  }

  return typeof base.uuid === 'string' && base.uuid.length > 0 ? base.uuid : undefined;
}

function toDatumWriteDto(
  datum: { baseReferencePoint?: { uuid?: unknown } | Record<string, unknown> } | null | undefined,
): TFactTrajectoryWriteDto['defaultMdDatum'] | undefined {
  const uuid = readBaseReferencePointUuid(datum);

  if (!uuid) {
    return undefined;
  }

  return {
    baseReferencePoint: {
      uuid,
      qualifiedType: 'ReferencePointInACrs',
    },
  };
}

function mapPointToStationDto(point: { data: TFactTrajectoryPoint }): TFactTrajectoryStationWriteDto {
  const dto: TFactTrajectoryStationWriteDto = {
    mdMeasuredDepth: numericField(point.data.md),
    incl: numericField(point.data.inc),
    azi: numericField(point.data.azi),
    mtf: numericField(point.data.mtf),
    gtf: numericField(point.data.gtf),
  };

  const stationId = resolveWriteId(point.data.stationId);

  if (stationId !== undefined) {
    dto.id = stationId;
  }

  if (point.data.typeTrajStation != null) {
    dto.typeTrajStation = point.data.typeTrajStation;
  }

  if (point.data.comment != null) {
    dto.appComment = point.data.comment;
  }

  return dto;
}

export function mapEntityToFactWriteDto(entity: FactTrajectoryEntity): TFactTrajectoryWriteDto {
  const data = entity.data;
  const isActive = Boolean(data.active ?? data.definitive);

  const dto: TFactTrajectoryWriteDto = {
    wellboreId: data.wellboreId,
    title: data.citation.title ?? '',
    trajectoryStations: entity.points.map(mapPointToStationDto),
  };

  const id = resolveWriteId(data.id);

  if (id !== undefined) {
    dto.id = id;
  }

  if (data.dtoIndex !== undefined) {
    dto.dtoIndex = data.dtoIndex;
  }

  if (data.objectVersion) {
    dto.objectVersion = data.objectVersion;
  }

  if (data.citation.description !== undefined) {
    dto.description = data.citation.description;
  }

  dto.activeStatus = isActive ? 'ACTIVE' : 'INACTIVE';

  if (data.finalTraj !== undefined) {
    dto.finalTraj = data.finalTraj;
  }

  if (data.parentTrajectoryId !== undefined) {
    dto.parentTrajectoryId = data.parentTrajectoryId;
  }

  const mdTieOn = measureValue(data.mdTieOn?.measuredDepth);

  if (mdTieOn !== undefined) {
    dto.mdTieOn = mdTieOn;
  }

  const mdTieOnUom = measureUom(data.mdTieOn?.measuredDepth);

  if (mdTieOnUom) {
    dto.mdTieOnUom = mdTieOnUom;
  }

  if (data.aziRef) {
    dto.aziRef = data.aziRef;
  }

  const gridConUsed = measureValue(data.gridConUsed);

  if (gridConUsed !== undefined) {
    dto.gridConUsed = gridConUsed;
  }

  const magDeclUsed = measureValue(data.magDeclUsed);

  if (magDeclUsed !== undefined) {
    dto.magDeclUsed = magDeclUsed;
  }

  const aziVertSect = measureValue(data.aziVertSect);

  if (aziVertSect !== undefined) {
    dto.aziVertSect = aziVertSect;
  }

  if (data.depthUomId !== undefined) {
    dto.depthUomId = data.depthUomId;
  }

  if (data.severityUomId !== undefined) {
    dto.severityUomId = data.severityUomId;
  }

  if (data.coordinatesUomId !== undefined) {
    dto.coordinatesUomId = data.coordinatesUomId;
  }

  const defaultMdDatum = toDatumWriteDto(data.defaultMdDatum);

  if (defaultMdDatum) {
    dto.defaultMdDatum = defaultMdDatum;
  }

  const defaultTvdDatum = toDatumWriteDto(data.defaultTvdDatum);

  if (defaultTvdDatum) {
    dto.defaultTvdDatum = defaultTvdDatum;
  }

  return dto;
}

const POINT_FIELDS: ReadonlySet<string> = new Set(FACT_TRAJECTORY_POINT_KEYS);

function isPointField(key: string): key is keyof TFactTrajectoryPoint {
  return POINT_FIELDS.has(key);
}

function toMeasure(value: number, previous?: TTrajectoryMeasure, uom?: string): TTrajectoryMeasure {
  return {
    value,
    uom: uom || previous?.uom,
    uomId: previous?.uomId,
  };
}

function toValueFromWriteDto(value: number | TTrajectoryValue | undefined): TTrajectoryValue | undefined {
  return toTrajectoryValue(value);
}

function buildDataPatchFromTrajectoryResponse(
  entity: FactTrajectoryEntity,
  response: TTrajectory & { activeStatus?: TFactTrajectoryWriteDto['activeStatus'] },
): Partial<Omit<TFactTrajectoryData, 'citation'>> & { citation?: Partial<TFactTrajectoryData['citation']> } {
  const patch: Partial<Omit<TFactTrajectoryData, 'citation'>> & {
    citation?: Partial<TFactTrajectoryData['citation']>;
  } = {
    id: response.id,
    objectVersion: response.objectVersion,
    wellboreId: response.wellboreId,
    finalTraj: response.finalTraj,
    parentTrajectoryId: response.parentTrajectoryId,
    defaultMdDatum: response.defaultMdDatum,
    defaultTvdDatum: response.defaultTvdDatum,
    aziRef: response.aziRef,
    depthUomId: response.depthUomId,
    severityUomId: response.severityUomId,
    coordinatesUomId: response.coordinatesUomId,
    citation: {
      title: response.citation?.title,
      description: response.citation?.description,
    },
  };

  const active = resolveActiveFlag(response);

  if (active !== undefined) {
    patch.active = active;
  }

  const gridConUsed = readTrajectoryValue(response.gridConUsed);
  if (gridConUsed !== undefined) {
    patch.gridConUsed = { value: gridConUsed };
  }

  const magDeclUsed = readTrajectoryValue(response.magDeclUsed);
  if (magDeclUsed !== undefined) {
    patch.magDeclUsed = { value: magDeclUsed };
  }

  const aziVertSect = readTrajectoryValue(response.aziVertSect);
  if (aziVertSect !== undefined) {
    patch.aziVertSect = { value: aziVertSect };
  }

  const mdTieOnValue = measureValue(response.mdTieOn?.measuredDepth);
  if (mdTieOnValue !== undefined) {
    patch.mdTieOn = {
      ...entity.data.mdTieOn,
      measuredDepth: toMeasure(mdTieOnValue, entity.data.mdTieOn?.measuredDepth, response.mdTieOn?.measuredDepth?.uom),
    };
  }

  return patch;
}

function readStationNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === 'object' && value && 'value' in value) {
    const nested = (value as { value?: unknown }).value;
    if (typeof nested === 'number' && !Number.isNaN(nested)) {
      return nested;
    }
  }

  return undefined;
}

function isApiTrajectoryStation(station: unknown): station is TTrajectoryStation {
  if (typeof station !== 'object' || !station) {
    return false;
  }

  if (!('md' in station) || typeof station.md !== 'object' || !station.md) {
    return false;
  }

  return 'measuredDepth' in station.md;
}

export function mapWriteStationToPointPatch(
  station: TFactTrajectoryStationWriteDto | TTrajectoryStation,
): Partial<TFactTrajectoryPoint> {
  if (isApiTrajectoryStation(station)) {
    return mapApiStationToFactPoint(station);
  }

  const patch: Partial<TFactTrajectoryPoint> = {};
  const record = station as TFactTrajectoryStationWriteDto & Partial<TFactTrajectoryPoint>;

  for (const key of Object.keys(record)) {
    switch (key) {
      case 'id':
        if (record.id !== undefined) {
          patch.stationId = record.id;
        }
        break;
      case 'mdMeasuredDepth':
        if (readStationNumber(record.mdMeasuredDepth) !== undefined) {
          patch.md = readStationNumber(record.mdMeasuredDepth);
        }
        break;
      case 'incl':
        if (readStationNumber(record.incl) !== undefined) {
          patch.inc = readStationNumber(record.incl);
        }
        break;
      case 'azi':
        if (readStationNumber(record.azi) !== undefined) {
          patch.azi = readStationNumber(record.azi);
        }
        break;
      case 'appComment':
        if (record.appComment !== undefined) {
          patch.comment = record.appComment;
        }
        break;
      default:
        if (isPointField(key) && record[key] !== undefined) {
          (patch as Record<string, unknown>)[key] = record[key];
        }
        break;
    }
  }

  return patch;
}

function buildDataPatchFromWriteDto(
  entity: FactTrajectoryEntity,
  response: TFactTrajectoryWriteDto,
): Partial<Omit<TFactTrajectoryData, 'citation'>> & { citation?: Partial<TFactTrajectoryData['citation']> } {
  const patch: Partial<Omit<TFactTrajectoryData, 'citation'>> & {
    citation?: Partial<TFactTrajectoryData['citation']>;
  } = {};
  const citation: Partial<TFactTrajectoryData['citation']> = {};

  for (const key of Object.keys(response) as Array<keyof TFactTrajectoryWriteDto>) {
    switch (key) {
      case 'id':
        if (response.id !== undefined) {
          patch.id = response.id;
        }
        break;
      case 'dtoIndex':
        if (response.dtoIndex !== undefined) {
          patch.dtoIndex = response.dtoIndex;
        }
        break;
      case 'objectVersion':
        if (response.objectVersion !== undefined) {
          patch.objectVersion = response.objectVersion;
        }
        break;
      case 'wellboreId':
        if (response.wellboreId !== undefined) {
          patch.wellboreId = response.wellboreId;
        }
        break;
      case 'title':
        if (response.title !== undefined) {
          citation.title = response.title;
        }
        break;
      case 'description':
        if (response.description !== undefined) {
          citation.description = response.description;
        }
        break;
      case 'activeStatus':
        if (response.activeStatus !== undefined) {
          patch.active = response.activeStatus === 'ACTIVE';
        }
        break;
      case 'finalTraj':
        if (response.finalTraj !== undefined) {
          patch.finalTraj = response.finalTraj;
        }
        break;
      case 'parentTrajectoryId':
        if (response.parentTrajectoryId !== undefined) {
          patch.parentTrajectoryId = response.parentTrajectoryId;
        }
        break;
      case 'aziRef':
        if (response.aziRef !== undefined) {
          patch.aziRef = response.aziRef;
        }
        break;
      case 'gridConUsed':
        if (response.gridConUsed !== undefined) {
          patch.gridConUsed = toValueFromWriteDto(response.gridConUsed);
        }
        break;
      case 'magDeclUsed':
        if (response.magDeclUsed !== undefined) {
          patch.magDeclUsed = toValueFromWriteDto(response.magDeclUsed);
        }
        break;
      case 'aziVertSect':
        if (response.aziVertSect !== undefined) {
          patch.aziVertSect = toValueFromWriteDto(response.aziVertSect);
        }
        break;
      case 'depthUomId':
        if (response.depthUomId !== undefined) {
          patch.depthUomId = response.depthUomId;
        }
        break;
      case 'severityUomId':
        if (response.severityUomId !== undefined) {
          patch.severityUomId = response.severityUomId;
        }
        break;
      case 'coordinatesUomId':
        if (response.coordinatesUomId !== undefined) {
          patch.coordinatesUomId = response.coordinatesUomId;
        }
        break;
      case 'defaultMdDatum':
        if (response.defaultMdDatum !== undefined) {
          patch.defaultMdDatum = {
            baseReferencePoint: response.defaultMdDatum.baseReferencePoint,
          };
        }
        break;
      case 'defaultTvdDatum':
        if (response.defaultTvdDatum !== undefined) {
          patch.defaultTvdDatum = {
            baseReferencePoint: response.defaultTvdDatum.baseReferencePoint,
          };
        }
        break;
      default:
        break;
    }
  }

  if (response.mdTieOn !== undefined || response.mdTieOnUom !== undefined) {
    const previous = entity.data.mdTieOn?.measuredDepth;
    const value = response.mdTieOn !== undefined ? response.mdTieOn : previous?.value;
    const uom = response.mdTieOnUom !== undefined ? response.mdTieOnUom : previous?.uom;

    if (typeof value === 'number') {
      patch.mdTieOn = {
        ...entity.data.mdTieOn,
        measuredDepth: toMeasure(value, previous, uom),
      };
    }
  }

  if (Object.keys(citation).length > 0) {
    patch.citation = citation;
  }

  return patch;
}

export function applyFactWriteDtoToEntity(
  entity: FactTrajectoryEntity,
  response: TFactTrajectoryWriteDto | TTrajectory | null | undefined,
): void {
  if (!response) {
    entity.commitData();
    return;
  }

  const patch =
    'citation' in response
      ? buildDataPatchFromTrajectoryResponse(entity, response)
      : buildDataPatchFromWriteDto(entity, response);
  entity.updateData(patch);

  if (Array.isArray(response.trajectoryStations)) {
    entity.syncPointsFromPatches(response.trajectoryStations.map((station) => mapWriteStationToPointPatch(station)));
  }

  entity.commitData();
}
